import { Component, ElementRef, HostListener, inject, NgZone, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  DefaultMenuItem,
  GetMainMenuItemsParams,
  GridOptions,
  ITooltipParams,
  MenuItemDef,
  ModuleRegistry,
  PinnedRowModule,
  RowApiModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  TextEditorModule,
  TooltipModule,
  ColumnApiModule
} from 'ag-grid-community';


import {
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  RowGroupingPanelModule,
  RowGroupingModule,
  PivotModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,
  SetFilterModule,
  AggregationModule
} from 'ag-grid-enterprise';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments';
import { FormsModule } from '@angular/forms';
import { AlertService } from '../../shared/alert.service';
import { AppAlertComponent } from '../../shared/components/app-alert/app-alert.component';
// import {
//   ServerSideRowModelModule,
//   AggregationModule,
//   ColumnsToolPanelModule,
//   FiltersToolPanelModule,
//   SetFilterModule,
//   MultiFilterModule,
//   NumberFilterModule,
//   TextFilterModule,
//   RowGroupingModule,
//   MenuModule,
//   StatusBarModule,
//   SideBarModule
// } from 'ag-grid-enterprise';

// ModuleRegistry.registerModules([
//   ServerSideRowModelModule,
//   AggregationModule,
//   PinnedRowModule,
//   ColumnsToolPanelModule,
//   FiltersToolPanelModule,
//   SetFilterModule,
//   MultiFilterModule,
//   NumberFilterModule,
//   TextFilterModule,
//   RowGroupingModule,
//   MenuModule,
//   StatusBarModule,
//   SideBarModule,
//   ColumnApiModule
// ]);
ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  RowApiModule,
  ColumnApiModule,

  // Filters
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  SetFilterModule,

  // Editing (user-typed cell expressions)
  TextEditorModule,
  TooltipModule,

  // Grouping
  RowGroupingModule,
  RowGroupingPanelModule,

  // Pivot
  PivotModule,

  // Aggregation
  AggregationModule,

  // Panels
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,

  // Footer
  PinnedRowModule
]);

@Component({
  selector: 'app-homepage',
  standalone: true,
  imports: [CommonModule, AgGridAngular, FormsModule, AppAlertComponent],
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.css']
})
export class HomepageComponent {
  constructor(private alertService: AlertService, private elementRef: ElementRef) { }
  apiUrl = environment.apiUrl;
  private readonly zone = inject(NgZone);

  @ViewChild('userProfile') userProfileRef?: ElementRef<HTMLElement>;

  gridApi: any;
  selectedReportId: number = 0;
  savedReports: any[] = [];
  gridReadyForState = false;
  showSaveBox = false;
  displayUserName: string = '';
  menuOpen = false;

  private dropPanelScrollSetup = false;
  private dropPanelScrollSetupRetries = 0;
  private rowGroupScrollList: HTMLElement | null = null;
  private rowGroupPrevBtn: HTMLButtonElement | null = null;
  private rowGroupNextBtn: HTMLButtonElement | null = null;
  private rowGroupResizeObserver: ResizeObserver | null = null;

  /** Shown under the grid so totals are visible even if pinned rows fail (e.g. SSRM + zone issues). */
  grandTotalUiEntries: { key: string; value: string }[] = [];

  /** TEMP: set true to show the pinned bottom grand-total row again. */
  private readonly showGrandTotalFooter = false;

  /**
   * Full-report grand total (root level). Kept while drilling down so the footer matches Flexmonster
   * (parent totals stay visible; child responses are not used to replace this row).
   */
  private cachedRootGrandTotalRow: Record<string, unknown> | null = null;

  /** Keyset cursor for leaf SSRM scroll (cleared when startRow === 0). */
  private leafCursorSortValue: string | null = null;
  private leafCursorLastId: number | null = null;
  private leafDefaultSortColumn: string = 'ShowStartDateTime';
  private lockedHierarchyGroupCols: string[] = [];
  private applyingLockedGroup = false;

  selectedUserId: string = '';
  reportName: string = '';
  ReportAPIId: string = '';
  isExporting = false;
  isAppLoading = false;
  private hideLoaderAfterPaint = false;
  selectedSavedReportId: number | null = null;
  showUpdateConfirm = false;
  isUpdating = false;

  private baseColumnDefs: ColDef[] = [];
  calculatedColumns: { Name: string; Formula: string }[] = [];
  showCalcDialog = false;
  editingCalcName: string | null = null;
  calcName = '';
  calcFormula = '';
  calcError = '';


  // columnDefs: ColDef[] = [
  //   { field: 'AccountID' },
  //   { field: 'TerritoryName' },
  //   { field: 'StateName', rowGroup: false },
  //   { field: 'CityName' },
  //   { field: 'CompanyName' },
  //   { field: 'ScreenName' },
  //   { field: 'MovieName' },
  //   {
  //     field: 'TicketSaleAmount', filter: 'agNumberColumnFilter',
  //     enableValue: true, aggFunc: 'sum'
  //   },
  //   {
  //     field: 'TicketSaleCount', filter: 'agNumberColumnFilter',
  //     enableValue: true, aggFunc: 'sum'
  //   }
  // ];

  ngOnInit() {
    this.showAppLoader();
    this.loadCurrentUser();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.menuOpen) {
      return;
    }

    const profileEl = this.userProfileRef?.nativeElement;
    const target = event.target as Node | null;
    if (profileEl && target && !profileEl.contains(target)) {
      this.menuOpen = false;
    }
  }

  toggleProfileMenu(event: MouseEvent) {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  get calcSourceFields(): string[] {
    return this.baseColumnDefs
      .map(c => (c.field || c.colId) as string)
      .filter(id => !!id);
  }

  private cellTooltipValue(params: ITooltipParams): string | undefined {
    const raw = params.valueFormatted ?? params.value;
    if (raw == null || raw === '') {
      return undefined;
    }
    return String(raw);
  }

  private showAppLoader() {
    this.zone.run(() => {
      this.isAppLoading = true;
      this.hideLoaderAfterPaint = false;
    });
  }

  private hideAppLoader() {
    this.zone.run(() => {
      this.isAppLoading = false;
      this.hideLoaderAfterPaint = false;
    });
  }

  private onGridPainted() {
    if (!this.isAppLoading || !this.hideLoaderAfterPaint) {
      return;
    }
    this.hideAppLoader();
  }

  columnDefs: ColDef[] = [];

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      buttons: ['apply', 'clear', 'reset'],
      closeOnApply: true,
      maxNumConditions: 2
    },
    floatingFilter: false,
    enableRowGroup: true,
    enablePivot: true,
    // Users pin/unpin freely via column menu (Pin Column); never lock freeze.
    lockPinned: false,
    tooltipValueGetter: (params: ITooltipParams) => this.cellTooltipValue(params),
    headerTooltipValueGetter: (params) => {
      const def = params.colDef as ColDef | undefined;
      return String(def?.headerName || def?.field || '');
    },
    // Allow typing =data.Field expressions; never edit pinned grand-total row
    editable: (params) => !params.node?.rowPinned
  };

  gridOptions: GridOptions = {
    rowModelType: 'serverSide',
    cacheBlockSize: 200,
    maxBlocksInCache: 3,
    blockLoadDebounceMillis: 100,
    pivotMode: false,
    rowGroupPanelShow: 'always',
    pivotPanelShow: 'always',
    groupDisplayType: 'multipleColumns',
    // Keep the theater (group) row visible when expanded; children render on the next rows.
    groupHideOpenParents: false,
    groupHideParentOfSingleChild: false,
    // showOpenedGroup: true,
    maintainColumnOrder: true,
    pinnedBottomRowData: [],
    // Evaluate cell values that start with "=" as expressions (session-only for SSRM)
    enableCellExpressions: true,
    tooltipShowMode: 'whenTruncated',
    tooltipShowDelay: 400,
    autoGroupColumnDef: {
      cellRendererParams: { suppressCount: true },
      tooltipValueGetter: (params: ITooltipParams) => this.cellTooltipValue(params)
    },

    // SSRM child count from API (grouping rows only). Must return number (GetChildCount).
    getChildCount: (dataItem: any): number => HomepageComponent.readChildGroupCount(dataItem),
    // Nested 1:1 groups; also expanded after each store load in expandNestedSingleChildGroups.
    // Never auto-open the last row-group level (avoids group + identical leaf clutter).
    isServerSideGroupOpenByDefault: (params) => {
      if (params.api?.isPivotMode?.()) {
        return false;
      }
      if (HomepageComponent.isLastRowGroupLevel(params.api, params.rowNode)) {
        return false;
      }
      return HomepageComponent.shouldAutoExpandGroup(params.rowNode, params.data);
    },

    // SSRM does not support grandTotalRow; SQL totals are shown in pinned bottom rows.
    getRowStyle: (params) => {
      if (params.node?.rowPinned === 'bottom') {
        return { fontWeight: 'bold', backgroundColor: 'rgba(255, 255, 255, 0.06)' };
      }
      return undefined;
    },

    // Hierarchy depth classes → slate shades in styles.css (dark → lighter).
    rowClassRules: {
      'hierarchy-level-0': (p) =>
        !p.node?.rowPinned && !!p.node?.group && (p.node?.level ?? -1) === 0,
      'hierarchy-level-1': (p) =>
        !p.node?.rowPinned && !!p.node?.group && (p.node?.level ?? -1) === 1,
      'hierarchy-level-2': (p) =>
        !p.node?.rowPinned && !!p.node?.group && (p.node?.level ?? -1) === 2,
      'hierarchy-level-3': (p) =>
        !p.node?.rowPinned && !!p.node?.group && (p.node?.level ?? -1) >= 3,
      'hierarchy-level-leaf': (p) =>
        !p.node?.rowPinned && !p.node?.group,
      'hierarchy-level-pinned': (p) => !!p.node?.rowPinned,
    },

    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filters',
          toolPanel: 'agFiltersToolPanel',
        }
      ],
      defaultToolPanel: 'columns'
    }
  };

  loadReport(reportId: string) {
    this.selectedReportId = Number(reportId);
    this.loadColumns(this.selectedReportId);
  }

  onGridReady(params: any) {
    this.gridApi = params.api;

    this.setupDropPanelScroll();

    this.gridApi.addEventListener('columnRowGroupChanged', () => {
      this.applyLockedHierarchyGroup();
      this.updateRowGroupScrollButtons();
    });
    this.gridApi.addEventListener('columnVisible', (event: any) => {
      this.ensureLockedHierarchyColumnsHidden();
      this.syncDropZonesWithColumnVisibility(event);
    });
    this.gridApi.addEventListener('columnPivotChanged', () => {
      this.updateRowGroupScrollButtons();
    });
    this.gridApi.addEventListener('gridSizeChanged', () => {
      this.updateRowGroupScrollButtons();
    });
    this.gridApi.addEventListener('storeRefreshed', () => {
      this.expandNestedSingleChildGroups(this.gridApi);
    });
    this.gridApi.addEventListener('rowGroupOpened', (event: any) => {
      if (!event?.expanded) {
        return;
      }
      setTimeout(() => this.expandDirectSingleChildGroups(event.node), 0);
    });
    this.gridApi.addEventListener('firstDataRendered', () => {
      this.onGridPainted();
    });
    this.gridApi.addEventListener('modelUpdated', () => {
      this.onGridPainted();
    });

    // params.api.setGridOption(
    //   'serverSideDatasource',
    //   this.createDatasource()
    // );

    // this.loadColumns(this.selectedReportId);

    // Columns may finish loading before the grid is ready — apply soft pins then.
    if (this.baseColumnDefs.length > 0) {
      this.applyLockedHierarchyGroup();
      this.applyStarterLeftPinsIfNeeded();
    }

  }

  private static readChildGroupCount(data: any): number {
    if (data == null) {
      return 0;
    }
    const raw = data.ChildGroupCount ?? data.childGroupCount;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  /**
   * Hierarchy display level from metadata HierarchyGrain (1-based → 0-based AG Grid level).
   * -1 = not a level-scoped parent attribute.
   */
  private static parentAttributeGroupLevelFromGrain(hierarchyGrain: number | null | undefined): number {
    const g = Number(hierarchyGrain);
    if (!Number.isFinite(g) || g < 1) {
      return -1;
    }
    return g - 1;
  }

  /** True when this node is at the deepest configured row-group column. */
  private static isLastRowGroupLevel(api: any, rowNode: any): boolean {
    const groupCount = api?.getRowGroupColumns?.()?.length ?? 0;
    if (groupCount <= 0) {
      return false;
    }
    return (rowNode?.level ?? -1) >= groupCount - 1;
  }

  /** Auto-expand nested 1:1 groups only (not root — avoids request storm on first paint). */
  private static shouldAutoExpandGroup(rowNode: any, data?: any): boolean {
    if ((rowNode?.level ?? 0) <= 0) {
      return false;
    }
    return HomepageComponent.readChildGroupCount(data ?? rowNode?.data) === 1;
  }

  /** After a parent is expanded, open loaded child groups that have exactly one nested group. */
  private expandDirectSingleChildGroups(parent: any): void {
    if (!parent || (parent.level ?? -1) < 0) {
      return;
    }
    for (const node of this.getDirectChildGroupNodes(parent)) {
      if (!node || node.expanded || node.stub) {
        continue;
      }
      if (HomepageComponent.isLastRowGroupLevel(this.gridApi, node)) {
        continue;
      }
      if (HomepageComponent.isSingleChildGroup(node)) {
        node.setExpanded(true, undefined, true);
      }
    }
  }

  private getDirectChildGroupNodes(parent: any): any[] {
    const out: any[] = [];
    const store = parent?.childStore;
    if (store && typeof store.forEachNodeDeep === 'function') {
      store.forEachNodeDeep((node: any) => {
        if (node?.parent === parent) {
          out.push(node);
        }
      });
      return out;
    }
    this.gridApi?.forEachNode?.((node: any) => {
      if (node?.parent === parent) {
        out.push(node);
      }
    });
    return out;
  }

  private static isSingleChildGroup(node: any): boolean {
    if (Number(node?.allChildrenCount) === 1) {
      return true;
    }
    return HomepageComponent.readChildGroupCount(node?.data) === 1;
  }

  /** After a parent is expanded, open loaded child groups that have exactly one nested group. */
  private expandNestedSingleChildGroups(api: any): void {
    if (!api || api.isDestroyed?.() || api.isPivotMode?.()) {
      return;
    }
    api.forEachNode?.((node: any) => {
      if (!node || node.expanded || node.stub) {
        return;
      }
      if ((node.parent?.level ?? -1) < 0) {
        return;
      }
      if (HomepageComponent.isLastRowGroupLevel(api, node)) {
        return;
      }
      if (HomepageComponent.isSingleChildGroup(node)) {
        node.setExpanded(true, undefined, true);
      }
    });
  }

  private parseGrandTotalPayload(data: any): Record<string, unknown> | null {
    let raw = data?.GrandTotal ?? data?.grandTotal;
    const jsonStr = data?.GrandTotalJson ?? data?.grandTotalJson;
    if (
      (raw === undefined || raw === null) &&
      typeof jsonStr === 'string' &&
      jsonStr.length > 0
    ) {
      try {
        raw = JSON.parse(jsonStr) as Record<string, unknown>;
      } catch {
        raw = null;
      }
    }
    if (raw === undefined || raw === null || typeof raw !== 'object') {
      return null;
    }
    return raw as Record<string, unknown>;
  }

  /** Put "Grand Total" on the first row-group column at root so the label shows in the hierarchy. */
  private normalizeGrandTotalForRootLabel(
    row: Record<string, unknown>,
    request: { rowGroupCols?: { field?: string }[] }
  ): Record<string, unknown> {
    const out = { ...row };
    const groupField = request?.rowGroupCols?.[0]?.field;
    if (!groupField) {
      return out;
    }
    const cur = out[groupField];
    if (cur !== undefined && cur !== null && String(cur).trim() !== '') {
      return out;
    }
    out[groupField] = 'Grand Total';
    return out;
  }

  /**
   * Root loads refresh `cachedRootGrandTotalRow`. Drill-down loads only re-apply the cached row so
   * the footer still shows the full-report grand total (not the slice total from the child request).
   */
  private syncGrandTotalFooterLikeFlexmonster(api: any, data: any, request: any) {
    // Temporary hide: clear footer and skip painting totals.
    if (!this.showGrandTotalFooter) {
      this.cachedRootGrandTotalRow = null;
      this.grandTotalUiEntries = [];
      api?.setGridOption?.('pinnedBottomRowData', []);
      return;
    }

    const atRoot = (request?.groupKeys?.length ?? 0) === 0;

    if (atRoot) {
      const parsed = this.parseGrandTotalPayload(data);
      if (parsed && Object.keys(parsed).length) {
        this.cachedRootGrandTotalRow = this.normalizeGrandTotalForRootLabel(parsed, request);
      } else {
        this.cachedRootGrandTotalRow = null;
      }
    }

    if (this.cachedRootGrandTotalRow && Object.keys(this.cachedRootGrandTotalRow).length) {
      api.setGridOption('pinnedBottomRowData', [{ ...this.cachedRootGrandTotalRow }]);
    } else {
      api.setGridOption('pinnedBottomRowData', []);
    }

    const row = this.cachedRootGrandTotalRow;
    if (!row || !Object.keys(row).length) {
      this.grandTotalUiEntries = [];
      return;
    }
    this.grandTotalUiEntries = Object.entries(row).map(([key, val]) => ({
      key,
      value: val === null || val === undefined ? '—' : String(val),
    }));
  }

  /** Authenticated API helper — always sends cookies; handles 401 mid-session. */
  private apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers || {});
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return fetch(input, {
      ...init,
      headers,
      credentials: 'include'
    }).then(res => {
      if (res.status === 401) {
        this.handleSessionExpired();
        throw new Error('Unauthorized');
      }
      return res;
    });
  }

  private handleSessionExpired(): void {
    this.alertService.error(
      'Your report session has expired.\nPlease open Analysis Report again from Impact FnB.'
    );
    setTimeout(() => {
      window.close();
      window.location.href = 'about:blank';
    }, 2000);
  }

  createDatasource() {
    return {
      getRows: (gridParams: any) => {
        const startRow = gridParams.request.startRow ?? 0;
        const endRow = gridParams.request.endRow ?? 0;
        const atRoot = (gridParams.request.groupKeys?.length ?? 0) === 0;
        // Pivot Mode ON with empty Column Labels is not an active pivot query.
        const pivotCols = gridParams.request.pivotCols || [];
        const activePivot =
          !!gridParams.request.pivotMode && pivotCols.length > 0;
        const isLeafRequest =
          !(gridParams.request.rowGroupCols?.length > (gridParams.request.groupKeys?.length ?? 0))
          && !activePivot;

        if (startRow === 0) {
          this.leafCursorSortValue = null;
          this.leafCursorLastId = null;
        }
        if (startRow === 0 && atRoot) {
          this.showAppLoader();
        }
        const groupKeys = this.normalizeHierarchyGroupKeys(
          gridParams.request.groupKeys,
          gridParams.request.rowGroupCols,
          gridParams.parentNode
        );
        const rowGroupCols = this.ensureHierarchyRowGroupCols(
          gridParams.request.rowGroupCols
        );
        const body: Record<string, unknown> = {
          ReportId: this.selectedReportId,
          StartRow: startRow,
          EndRow: endRow,
          FilterModel: gridParams.request.filterModel,
          // SortModel: gridParams.request.sortModel,
          SortModel: (gridParams.request.sortModel || [])
            .filter((x: any) => x && x.colId && !String(x.colId).startsWith('ag-Grid-AutoColumn'))
            .map((x: any) => ({
              ColId: x.colId,
              Sort: x.sort
            })),
          RowGroupCols: rowGroupCols,
          GroupKeys: groupKeys,
          PivotCols: pivotCols,
          ValueCols: gridParams.request.valueCols,
          PivotMode: activePivot,
          CalculatedColumns: this.getCalculatedColumnsPayload()
        };

        if (isLeafRequest && startRow > 0 && this.leafCursorSortValue != null) {
          body['LastSortValue'] = this.leafCursorSortValue;
          if (this.leafCursorLastId != null) {
            body['LastId'] = this.leafCursorLastId;
          }
        }

        this.apiFetch(`${this.apiUrl}/ImpactReport/GetMovieSales`, {
          method: 'POST',
          body: JSON.stringify(body)
        })
          .then(res => res.json())
          .then(data => {
            const rows = data.Rows ?? data.rows ?? [];
            const sortCol =
              data.DefaultSortColumn ?? data.defaultSortColumn ?? this.leafDefaultSortColumn;
            if (sortCol) {
              this.leafDefaultSortColumn = sortCol;
            }

            if (isLeafRequest && Array.isArray(rows) && rows.length > 0) {
              const requestSortCol = gridParams.request.sortModel?.[0]?.colId as string | undefined;
              const activeSortCol = requestSortCol || sortCol;
              const last = rows[rows.length - 1] as Record<string, unknown>;
              const sortVal = last[activeSortCol] ?? last[Object.keys(last).find(
                k => k.toLowerCase() === String(activeSortCol).toLowerCase()
              ) ?? ''];
              if (sortVal !== undefined && sortVal !== null) {
                this.leafCursorSortValue = String(sortVal);
              }
              const idKey = Object.keys(last).find(k => k.toLowerCase() === 'id');
              if (idKey != null && last[idKey] != null && last[idKey] !== '') {
                const parsed = Number(last[idKey]);
                this.leafCursorLastId = Number.isFinite(parsed) ? parsed : null;
              } else {
                this.leafCursorLastId = null;
              }
            }

            if (startRow === 0 && atRoot) {
              this.hideLoaderAfterPaint = true;
            }

            gridParams.success({
              rowData: rows,
              rowCount: data.Total ?? data.total,
              pivotResultFields: data.PivotFields ?? data.pivotFields
            });

            if (startRow === 0 && atRoot) {
              queueMicrotask(() => this.onGridPainted());
            }

            const api = gridParams.api ?? this.gridApi;
            const req = gridParams.request;
            const parentNode = gridParams.parentNode;

            // Walk this store's children (does not depend on api.forEachNode).
            if (!atRoot && parentNode) {
              setTimeout(() => {
                this.zone.run(() => this.expandDirectSingleChildGroups(parentNode));
              }, 0);
            }

            // Leaf root: paint rows first; load grand total separately so SUM does not block.
            if (isLeafRequest && atRoot && startRow === 0) {
              this.fetchLeafGrandTotalAsync(api, req, body);
            } else {
              queueMicrotask(() =>
                this.zone.run(() => this.syncGrandTotalFooterLikeFlexmonster(api, data, req))
              );
            }
          })
          .catch(() => {
            if (startRow === 0 && atRoot) {
              this.hideAppLoader();
            }
            gridParams.fail();
          });
      }
    };
  }

  /** Async leaf grand total — must not delay gridParams.success. */
  private fetchLeafGrandTotalAsync(api: any, request: any, dataBody: Record<string, unknown>) {
    this.apiFetch(`${this.apiUrl}/ImpactReport/GetMovieSalesGrandTotal`, {
      method: 'POST',
      body: JSON.stringify({
        ReportId: dataBody['ReportId'],
        FilterModel: dataBody['FilterModel'],
        RowGroupCols: dataBody['RowGroupCols'],
        GroupKeys: dataBody['GroupKeys'],
        ValueCols: dataBody['ValueCols'],
        PivotMode: false,
        CalculatedColumns: dataBody['CalculatedColumns']
      })
    })
      .then(res => res.json())
      .then(gtData => {
        this.zone.run(() => this.syncGrandTotalFooterLikeFlexmonster(api, gtData, request));
      })
      .catch(() => {
        // Rows already shown; footer is best-effort.
      });
  }

  loadColumns(reportId: number) {
    this.showAppLoader();
    this.apiFetch(`${this.apiUrl}/ImpactReport/GetReportColumns?reportId=${reportId}`)
      .then(res => res.json())
      .then(data => {
        // Meta object from API; tolerate legacy flat array for older backends.
        const meta = Array.isArray(data)
          ? {
              isHierarchical: false,
              parentKeyColumns: [] as string[],
              childKeyColumns: [] as string[],
              childTableName: '',
              columns: data as any[],
            }
          : {
              isHierarchical: !!(data?.isHierarchical ?? data?.IsHierarchical),
              parentKeyColumns: (data?.parentKeyColumns ?? data?.ParentKeyColumns ?? []) as string[],
              childKeyColumns: (data?.childKeyColumns ?? data?.ChildKeyColumns ?? []) as string[],
              childTableName: String(
                data?.childTableName ?? data?.ChildTableName ?? ''),
              columns: (data?.columns ?? data?.Columns ?? []) as any[],
            };

        const columnRows = meta.columns || [];
        const isHierarchical = !!meta.isHierarchical
          || (!!meta.childTableName && meta.childTableName.trim().length > 0);

        const colName = (c: any) => String(c.ColumnName ?? c.columnName ?? '');
        const findCol = (name: string) =>
          columnRows.find((c: any) => colName(c).toLowerCase() === name.toLowerCase());

        this.lockedHierarchyGroupCols = isHierarchical
          ? (meta.parentKeyColumns || [])
              .map(name => findCol(name)?.ColumnName ?? findCol(name)?.columnName ?? name)
              .map((name: string) => String(name))
              .filter((name: string) => !!name)
          : [];

        const lockedLower = new Set(
          this.lockedHierarchyGroupCols.map(n => n.toLowerCase()));

        const childTableLower = String(meta.childTableName || '').toLowerCase();
        const childKeyLower = new Set(
          (meta.childKeyColumns || []).map(k => String(k || '').toLowerCase()).filter(Boolean));

        // One ColDef per ColumnName (API may still return shared names once).
        const seenCol = new Set<string>();
        const cols: ColDef[] = [];
        for (const c of columnRows) {
          const name = colName(c);
          const lower = name.toLowerCase();
          if (!name || seenCol.has(lower)) {
            continue;
          }
          seenCol.add(lower);

          const source = String(c.SourceTable ?? c.sourceTable ?? '').toLowerCase();
          // Hide child-table hierarchy key columns using metadata keys (not fixed names).
          if (isHierarchical
            && childTableLower
            && source === childTableLower
            && childKeyLower.has(lower)) {
            continue;
          }

          let col: ColDef = {
            field: name,
            colId: name,
            enableRowGroup: (c.IsGroupable ?? c.isGroupable) == 1 && !lockedLower.has(lower),
            enablePivot: (c.IsPivotable ?? c.isPivotable) == 1 && !lockedLower.has(lower),
          };

          const isLockedHierarchy = lockedLower.has(lower);
          if (isLockedHierarchy) {
            col.rowGroup = true;
            col.enableRowGroup = true;
            col.hide = true;
            col.lockVisible = true;
            col.enableValue = false;
            col.aggFunc = null;
          } else if ((c.IsAggregatable ?? c.isAggregatable) == 1) {
            col.enableValue = true;
            col.aggFunc = 'sum';
          }

          const dataType = c.DataType ?? c.dataType;
          if (this.isNumberColumnType(dataType)) {
            col.filter = 'agNumberColumnFilter';
            col.filterParams = this.getStandardFilterParams();
          } else if (this.isDateColumnType(dataType)) {
            col.filter = 'agDateColumnFilter';
            col.filterParams = this.getDateFilterParams();
          } else {
            col.filter = 'agTextColumnFilter';
            col.filterParams = this.getStandardFilterParams();
          }

          // Hierarchical only: labels at exact HierarchyGrain; numeric measures at that
          // grain and deeper group rows (e.g. Ad_Inventory_Dur on day + theater).
          if (isHierarchical && !isLockedHierarchy) {
            const attrLevel = HomepageComponent.parentAttributeGroupLevelFromGrain(
              c.HierarchyGrain ?? c.hierarchyGrain);
            if (attrLevel >= 0) {
              const fieldName = name;
              const isMeasure = this.isNumberColumnType(dataType);
              col.valueGetter = (params: any) => {
                if (params?.node?.rowPinned) {
                  return params.data?.[fieldName];
                }
                const level = params?.node?.level;
                if (typeof level !== 'number') {
                  return null;
                }
                if (isMeasure) {
                  if (level < attrLevel) {
                    return null;
                  }
                } else if (level !== attrLevel) {
                  return null;
                }
                if (params?.node?.group === false) {
                  return null;
                }
                return params.data?.[fieldName] ?? null;
              };
            }
          }

          cols.push(col);
        }

        this.baseColumnDefs = cols;
        this.calculatedColumns = [];
        this.calcName = '';
        this.calcFormula = '';
        this.calcError = '';
        this.showCalcDialog = false;
        this.editingCalcName = null;
        this.syncCalculatedColumnDefs();

        if (this.gridApi) {
          this.cachedRootGrandTotalRow = null;
          this.grandTotalUiEntries = [];
          this.leafCursorSortValue = null;
          this.leafCursorLastId = null;

          // Do not paint group key values onto every child row (hierarchical only).
          if (isHierarchical) {
            this.gridApi.setGridOption('showOpenedGroup', false);
          }

          this.gridApi.setGridOption(
            'serverSideDatasource',
            this.createDatasource()
          );

          this.restoreDefaultAggregates();
          this.applyLockedHierarchyGroup();
          this.applyStarterLeftPinsIfNeeded();
          this.setupDropPanelScroll();
        }

        this.loadSavedReports();
      })
      .catch((error) => {
        this.hideAppLoader();
        if (error?.message !== 'Unauthorized') {
          console.error("Error loading columns", error);
        }
      });
  }

  /**
   * Soft-default left pins for common inventory columns on first open only.
   * Skips when any column is already pinned (saved layout / user choice).
   * Users add/remove freeze anytime via column menu → Pin Column (not ColDef hardcoding).
   */
  private static readonly STARTER_LEFT_PIN_ALIASES: string[][] = [
    ['property_id', 'propertyid'],
    ['property_name', 'propertyname'],
    ['day_wise_date', 'daywisedate'],
    // DB: Ad_Inventory_Dur (and common spelling variants)
    ['ad_inventory_dur', 'adinventorydur', 'ad_inventory_duration', 'adinventoryduration']
  ];

  private normalizeColKey(colId: string): string {
    return String(colId || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private applyStarterLeftPinsIfNeeded(): void {
    if (!this.gridApi) {
      return;
    }

    const columnState = this.gridApi.getColumnState() ?? [];
    if (columnState.some((s: { pinned?: string | null }) =>
      s.pinned === 'left' || s.pinned === 'right')) {
      return;
    }

    const byNorm = new Map<string, string>();
    for (const s of columnState) {
      const colId = s.colId;
      if (!colId) {
        continue;
      }
      byNorm.set(this.normalizeColKey(colId), colId);
    }

    const toPin: { colId: string; pinned: 'left' }[] = [];
    for (const aliases of HomepageComponent.STARTER_LEFT_PIN_ALIASES) {
      for (const alias of aliases) {
        const colId = byNorm.get(this.normalizeColKey(alias));
        if (colId) {
          toPin.push({ colId, pinned: 'left' });
          break;
        }
      }
    }

    if (toPin.length === 0) {
      return;
    }

    this.gridApi.applyColumnState({ state: toPin });
  }

  private isNumberColumnType(dataType: string | null | undefined): boolean {
    const t = String(dataType || '').trim().toLowerCase().split('(')[0];
    return t === 'int'
      || t === 'bigint'
      || t === 'smallint'
      || t === 'tinyint'
      || t === 'decimal'
      || t === 'numeric'
      || t === 'money'
      || t === 'smallmoney'
      || t === 'float'
      || t === 'real';
  }

  private isDateColumnType(dataType: string | null | undefined): boolean {
    const t = String(dataType || '').trim().toLowerCase();
    return t === 'date'
      || t === 'datetime'
      || t === 'datetime2'
      || t === 'smalldatetime'
      || t === 'datetimeoffset';
  }

  private getDateFilterParams() {
    return {
      browserDatePicker: true,
      defaultOption: 'equals',
      maxNumConditions: 2,
      // Per-filter Clear / Reset in column menu and Filters tool panel.
      buttons: ['apply', 'clear', 'reset'],
      closeOnApply: true,
      filterOptions: [
        'equals',
        'notEqual',
        'lessThan',
        'greaterThan',
        'inRange',
        // Built-in AG Grid date presets (do not wrap as custom 0-input options —
        // that disables the type dropdown in the Filters tool panel).
        'today',
        'yesterday',
        'thisWeek',
        'thisMonth',
        'thisYear',
        'blank',
        'notBlank'
      ]
    };
  }

  /** Shared filter chrome: Clear removes only this column's filter. */
  private getStandardFilterParams() {
    return {
      buttons: ['apply', 'clear', 'reset'],
      closeOnApply: true,
      maxNumConditions: 2
    };
  }

  private getCalculatedColumnsPayload() {
    return this.calculatedColumns.map(c => ({
      Name: c.Name,
      Formula: c.Formula
    }));
  }

  private buildCalcColDef(calc: { Name: string; Formula: string }): ColDef {
    return {
      field: calc.Name,
      colId: calc.Name,
      headerName: calc.Name,
      filter: 'agNumberColumnFilter',
      filterParams: this.getStandardFilterParams(),
      enableValue: true,
      aggFunc: 'sum',
      enableRowGroup: false,
      enablePivot: false,
      mainMenuItems: (params: GetMainMenuItemsParams) => {
        const items: (DefaultMenuItem | MenuItemDef)[] = [...(params.defaultItems || [])];
        items.push('separator');
        items.push({
          name: 'Edit formula',
          action: () => this.zone.run(() => this.openEditCalcDialog(calc.Name))
        });
        items.push({
          name: 'Delete column',
          action: () => this.zone.run(() => this.deleteCalculatedColumn(calc.Name))
        });
        return items;
      }
    };
  }

  private syncCalculatedColumnDefs() {
    this.columnDefs = [
      ...this.baseColumnDefs,
      ...this.calculatedColumns.map(c => this.buildCalcColDef(c))
    ];
    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
    }
  }

  private sanitizeCalcName(raw: string): string {
    return String(raw || '').trim().replace(/[^A-Za-z0-9_]/g, '');
  }

  private nextCalcName(): string {
    let n = this.calculatedColumns.length + 1;
    let name = `Calc_${n}`;
    const taken = new Set([
      ...this.calcSourceFields.map(f => f.toLowerCase()),
      ...this.calculatedColumns.map(c => c.Name.toLowerCase())
    ]);
    while (taken.has(name.toLowerCase())) {
      n += 1;
      name = `Calc_${n}`;
    }
    return name;
  }

  openCalcDialog() {
    if (!this.selectedReportId) {
      return;
    }
    this.editingCalcName = null;
    this.calcName = this.nextCalcName();
    this.calcFormula = '';
    this.calcError = '';
    this.showCalcDialog = true;
  }

  openEditCalcDialog(colId: string) {
    const existing = this.calculatedColumns.find(
      c => c.Name.toLowerCase() === String(colId || '').toLowerCase()
    );
    if (!existing) {
      return;
    }
    this.editingCalcName = existing.Name;
    this.calcName = existing.Name;
    this.calcFormula = existing.Formula;
    this.calcError = '';
    this.showCalcDialog = true;
  }

  closeCalcDialog() {
    this.showCalcDialog = false;
    this.calcError = '';
    this.editingCalcName = null;
  }

  insertCalcField(field: string) {
    const current = this.calcFormula || '';
    const needsSpace = current.length > 0 && !/\s$/.test(current) && !/[+\-*/(]$/.test(current);
    this.calcFormula = current + (needsSpace ? ' ' : '') + field;
  }

  private validateCalcFormula(formula: string): string | null {
    const allowed = new Set(this.calcSourceFields.map(f => f.toLowerCase()));
    let s = String(formula || '').trim();
    if (s.startsWith('=')) {
      s = s.slice(1).trim();
    }
    if (!s) {
      return 'Enter a formula using existing columns.';
    }

    type Tok = { kind: 'ident' | 'number' | 'op' | 'lparen' | 'rparen'; text: string };
    const tokens: Tok[] = [];
    let i = 0;
    while (i < s.length) {
      const ch = s[i];
      if (/\s/.test(ch)) {
        i += 1;
        continue;
      }
      if (ch === '(') {
        tokens.push({ kind: 'lparen', text: ch });
        i += 1;
        continue;
      }
      if (ch === ')') {
        tokens.push({ kind: 'rparen', text: ch });
        i += 1;
        continue;
      }
      if ('+-*/'.includes(ch)) {
        tokens.push({ kind: 'op', text: ch });
        i += 1;
        continue;
      }
      if (/[A-Za-z_]/.test(ch)) {
        let j = i + 1;
        while (j < s.length && /[A-Za-z0-9_]/.test(s[j])) {
          j += 1;
        }
        tokens.push({ kind: 'ident', text: s.slice(i, j) });
        i = j;
        continue;
      }
      if (/[0-9.]/.test(ch)) {
        let j = i;
        let dot = false;
        while (j < s.length && (/[0-9]/.test(s[j]) || (!dot && s[j] === '.'))) {
          if (s[j] === '.') {
            dot = true;
          }
          j += 1;
        }
        const num = s.slice(i, j);
        if (num === '.' || num.endsWith('.')) {
          return 'Invalid number in formula.';
        }
        tokens.push({ kind: 'number', text: num });
        i = j;
        continue;
      }
      return `Invalid character '${ch}' in formula.`;
    }

    if (tokens.length === 0) {
      return 'Enter a formula using existing columns.';
    }

    let pos = 0;
    const peek = () => tokens[pos];
    const take = (): Tok => {
      const t = peek();
      if (!t) {
        throw new Error('Incomplete formula.');
      }
      pos += 1;
      return t;
    };

    const parseExpr = (): void => {
      parseTerm();
      while (peek()?.kind === 'op' && (peek().text === '+' || peek().text === '-')) {
        take();
        parseTerm();
      }
    };
    const parseTerm = (): void => {
      parseFactor();
      while (peek()?.kind === 'op' && (peek().text === '*' || peek().text === '/')) {
        take();
        parseFactor();
      }
    };
    const parseFactor = (): void => {
      const t = peek();
      if (!t) {
        throw new Error('Incomplete formula.');
      }
      if (t.kind === 'op' && t.text === '-') {
        take();
        parseFactor();
        return;
      }
      if (t.kind === 'number') {
        take();
        return;
      }
      if (t.kind === 'ident') {
        const name = take().text;
        if (!allowed.has(name.toLowerCase())) {
          throw new Error(`Unknown column '${name}'.`);
        }
        return;
      }
      if (t.kind === 'lparen') {
        take();
        parseExpr();
        const close = peek();
        if (!close || close.kind !== 'rparen') {
          throw new Error('Missing closing parenthesis.');
        }
        take();
        return;
      }
      throw new Error('Invalid formula.');
    };

    try {
      parseExpr();
    } catch (e: any) {
      return e?.message || 'Invalid formula.';
    }
    if (pos !== tokens.length) {
      return 'Invalid formula.';
    }
    return null;
  }

  submitCalcDialog() {
    if (!this.selectedReportId || !this.gridApi) {
      return;
    }

    let name = this.sanitizeCalcName(this.calcName);
    if (!name) {
      name = this.editingCalcName || this.nextCalcName();
    }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
      this.calcError = 'Name must start with a letter and use only letters, digits, or underscore.';
      return;
    }
    const taken = new Set([
      ...this.calcSourceFields.map(f => f.toLowerCase()),
      ...this.calculatedColumns
        .filter(c => !this.editingCalcName
          || c.Name.toLowerCase() !== this.editingCalcName.toLowerCase())
        .map(c => c.Name.toLowerCase())
    ]);
    if (taken.has(name.toLowerCase())) {
      this.calcError = `Column name '${name}' is already used.`;
      return;
    }

    let formula = String(this.calcFormula || '').trim();
    if (formula.startsWith('=')) {
      formula = formula.slice(1).trim();
    }
    const formulaError = this.validateCalcFormula(formula);
    if (formulaError) {
      this.calcError = formulaError;
      return;
    }

    const oldName = this.editingCalcName;
    if (oldName) {
      this.calculatedColumns = this.calculatedColumns.map(c =>
        c.Name.toLowerCase() === oldName.toLowerCase()
          ? { Name: name, Formula: formula }
          : c
      );
    } else {
      this.calculatedColumns = [
        ...this.calculatedColumns,
        { Name: name, Formula: formula }
      ];
    }

    const pendingRefs = oldName
      ? this.captureCalculatedColumnRefs(oldName, name)
      : null;
    this.showCalcDialog = false;
    this.calcError = '';
    this.editingCalcName = null;
    this.syncCalculatedColumnDefs();
    if (pendingRefs) {
      this.applyCalculatedColumnRefs(pendingRefs);
    }
    this.reloadCalculatedColumnData();
  }

  deleteCalculatedColumn(colId: string) {
    const existing = this.calculatedColumns.find(
      c => c.Name.toLowerCase() === String(colId || '').toLowerCase()
    );
    if (!existing || !this.gridApi) {
      return;
    }
    this.calculatedColumns = this.calculatedColumns.filter(
      c => c.Name.toLowerCase() !== existing.Name.toLowerCase()
    );
    this.dropCalculatedColumnRefs(existing.Name);
    this.syncCalculatedColumnDefs();
    this.reloadCalculatedColumnData();
  }

  private reloadCalculatedColumnData() {
    this.cachedRootGrandTotalRow = null;
    this.grandTotalUiEntries = [];
    this.leafCursorSortValue = null;
    this.leafCursorLastId = null;
    if (!this.gridApi) {
      return;
    }
    this.showAppLoader();
    this.gridApi.setGridOption('serverSideDatasource', this.createDatasource());
    if (typeof this.gridApi.refreshServerSide === 'function') {
      this.gridApi.refreshServerSide({ purge: true });
    }
  }

  private captureCalculatedColumnRefs(oldName: string, newName: string): {
    filterModel: Record<string, unknown>;
    columnState: any[];
    valueIds: string[];
  } | null {
    if (!this.gridApi || oldName === newName) {
      return null;
    }

    const filterModel = { ...(this.gridApi.getFilterModel() ?? {}) };
    const oldKey = Object.keys(filterModel).find(
      k => k.toLowerCase() === oldName.toLowerCase()
    );
    if (oldKey) {
      filterModel[newName] = filterModel[oldKey];
      delete filterModel[oldKey];
    }

    const columnState = (this.gridApi.getColumnState() ?? []).map((s: any) =>
      String(s.colId || '').toLowerCase() === oldName.toLowerCase()
        ? { ...s, colId: newName }
        : s
    );
    const valueIds = (this.gridApi.getValueColumns() ?? []).map((c: any) => {
      const id = c.getColId();
      return String(id).toLowerCase() === oldName.toLowerCase() ? newName : id;
    });

    return { filterModel, columnState, valueIds };
  }

  private applyCalculatedColumnRefs(refs: {
    filterModel: Record<string, unknown>;
    columnState: any[];
    valueIds: string[];
  }) {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setFilterModel(refs.filterModel);
    this.gridApi.applyColumnState({ state: refs.columnState, applyOrder: true });
    if (refs.valueIds.length) {
      this.gridApi.setValueColumns(refs.valueIds);
    }
  }

  private dropCalculatedColumnRefs(colId: string) {
    if (!this.gridApi) {
      return;
    }
    const filterModel = { ...(this.gridApi.getFilterModel() ?? {}) };
    for (const key of Object.keys(filterModel)) {
      if (key.toLowerCase() === colId.toLowerCase()) {
        delete filterModel[key];
      }
    }
    this.gridApi.setFilterModel(filterModel);
    const valueIds = (this.gridApi.getValueColumns() ?? [])
      .map((c: any) => c.getColId())
      .filter((id: string) => String(id).toLowerCase() !== colId.toLowerCase());
    this.gridApi.setValueColumns(valueIds);
  }

  private restoreCalculatedColumns(
    list: { Name?: string; Formula?: string }[] | undefined
  ) {
    if (!Array.isArray(list) || list.length === 0) {
      this.calculatedColumns = [];
      this.syncCalculatedColumnDefs();
      return;
    }
    const restored: { Name: string; Formula: string }[] = [];
    const used = new Set<string>(this.calcSourceFields.map(f => f.toLowerCase()));
    for (const item of list) {
      const name = this.sanitizeCalcName(item?.Name || '');
      let formula = String(item?.Formula || '').trim();
      if (formula.startsWith('=')) {
        formula = formula.slice(1).trim();
      }
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name) || used.has(name.toLowerCase())) {
        continue;
      }
      if (this.validateCalcFormula(formula)) {
        continue;
      }
      used.add(name.toLowerCase());
      restored.push({ Name: name, Formula: formula });
    }
    this.calculatedColumns = restored;
    this.syncCalculatedColumnDefs();
  }

  private getDefaultAggregatableColIds(): string[] {
    const locked = new Set(
      (this.lockedHierarchyGroupCols || []).map(c => c.toLowerCase()));
    return (this.columnDefs || [])
      .filter(c => c.enableValue === true)
      .map(c => (c.colId || c.field) as string)
      .filter(id => !!id && !locked.has(String(id).toLowerCase()));
  }

  private restoreDefaultAggregates() {
    if (!this.gridApi) {
      return;
    }

    const defaultIds = this.getDefaultAggregatableColIds();
    if (!defaultIds.length) {
      return;
    }

    this.gridApi.applyColumnState({
      state: defaultIds.map(colId => ({
        colId,
        aggFunc: 'sum'
      }))
    });

    this.gridApi.setValueColumns(defaultIds);
  }

  private dateOnlyKey(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }

  private normalizeHierarchyGroupKeys(
    groupKeys: unknown[] | undefined,
    rowGroupCols: { field?: string; colId?: string }[] | undefined,
    parentNode: any
  ): unknown[] {
    const keys = [...(groupKeys ?? [])];
    const cols = rowGroupCols ?? [];
    for (let i = 0; i < keys.length && i < cols.length; i++) {
      const field = String(cols[i].field || cols[i].colId || '');
      if (/date/i.test(field)) {
        const dateKey = this.dateOnlyKey(keys[i]);
        if (dateKey) {
          keys[i] = dateKey;
        }
      }
    }

    if (this.lockedHierarchyGroupCols.length === 0 || !parentNode?.data) {
      return keys;
    }

    const gridHasLockedDate = (rowGroupCols ?? []).some(c =>
      this.lockedHierarchyGroupCols.slice(1).some(
        l => String(c.field || c.colId || '').toLowerCase() === l.toLowerCase()));
    if (gridHasLockedDate) {
      return keys;
    }

    const data = parentNode.data as Record<string, unknown>;
    for (let i = 0; i < this.lockedHierarchyGroupCols.length; i++) {
      const col = this.lockedHierarchyGroupCols[i];
      if (i < keys.length) {
        continue;
      }
      let raw = data[col];
      if (raw == null) {
        const found = Object.keys(data).find(k => k.toLowerCase() === col.toLowerCase());
        raw = found != null ? data[found] : null;
      }
      if (raw == null) {
        continue;
      }
      keys.push(this.dateOnlyKey(raw) ?? raw);
    }
    return keys;
  }

  private ensureHierarchyRowGroupCols(
    rowGroupCols: { field?: string; id?: string; displayName?: string }[] | undefined
  ): { field?: string; id?: string; displayName?: string }[] {
    const cols = [...(rowGroupCols ?? [])];
    if (this.lockedHierarchyGroupCols.length === 0) {
      return cols;
    }
    const have = new Set(cols.map(c => String(c.field || '').toLowerCase()));
    for (const locked of this.lockedHierarchyGroupCols) {
      if (!have.has(locked.toLowerCase())) {
        cols.push({ field: locked, id: locked, displayName: locked });
        have.add(locked.toLowerCase());
      }
    }
    const lockedLower = this.lockedHierarchyGroupCols.map(c => c.toLowerCase());
    cols.sort((a, b) => {
      const ai = lockedLower.indexOf(String(a.field || '').toLowerCase());
      const bi = lockedLower.indexOf(String(b.field || '').toLowerCase());
      const av = ai === -1 ? 1000 : ai;
      const bv = bi === -1 ? 1000 : bi;
      return av - bv;
    });
    return cols;
  }

  private applyLockedHierarchyGroup() {
    if (!this.gridApi || this.lockedHierarchyGroupCols.length === 0 || this.applyingLockedGroup) {
      return;
    }

    const locked = this.lockedHierarchyGroupCols;
    const lockedLower = new Set(locked.map(l => l.toLowerCase()));
    const current: string[] = (this.gridApi.getRowGroupColumns() || [])
      .map((c: any) => c.getColId() as string);
    const seen = new Set<string>();
    // Only keep non-hierarchy extras (never a second property_id / Theater_ID).
    const rest = current.filter(id => {
      const lower = String(id).toLowerCase();
      if (lockedLower.has(lower) || lower === 'daywisedate') {
        return false;
      }
      if (seen.has(lower)) {
        return false;
      }
      seen.add(lower);
      return true;
    });

    const desired = [...locked, ...rest];
    const same = desired.length === current.length
      && desired.every((id, i) =>
        current[i] && current[i].toLowerCase() === id.toLowerCase());
    if (same) {
      this.ensureLockedHierarchyColumnsHidden();
      return;
    }

    this.applyingLockedGroup = true;
    try {
      this.gridApi.setRowGroupColumns(desired);
      this.ensureLockedHierarchyColumnsHidden();
      const valueIds = (this.gridApi.getValueColumns() || [])
        .map((c: any) => c.getColId() as string)
        .filter((id: string) => !lockedLower.has(String(id).toLowerCase()));
      this.gridApi.setValueColumns(valueIds);
    } finally {
      this.applyingLockedGroup = false;
    }
  }

  /**
   * Locked hierarchy keys stay in Row Groups for SSRM drill, but their data
   * columns must remain hidden so Select All cannot add a second Theater_ID /
   * Day_Wise_Date next to the group columns.
   */
  private ensureLockedHierarchyColumnsHidden() {
    if (!this.gridApi || this.lockedHierarchyGroupCols.length === 0) {
      return;
    }

    const toHide = this.lockedHierarchyGroupCols.filter(id => {
      const col = this.gridApi.getColumn(id);
      return col && col.isVisible();
    });
    if (toHide.length === 0) {
      return;
    }

    this.gridApi.applyColumnState({
      state: toHide.map(colId => ({ colId, hide: true })),
      defaultState: undefined
    });
  }

  /**
   * Tool-panel "Select All" / uncheck only toggles visibility. Columns left in
   * Values / Column Labels would still drive aggregates. Drop them when hidden.
   * Locked hierarchy row groups stay (from parentKeyColumns meta, e.g.
   * property_id → Day_Wise_Date → Theater_ID) for parent/child drill.
   */
  private syncDropZonesWithColumnVisibility(_event?: any) {
    if (!this.gridApi || this.applyingLockedGroup) {
      return;
    }

    // Always reconcile against full column visibility (Select All / Unselect All
    // may pass a partial event list depending on AG Grid version).
    const consider = (this.gridApi.getColumns() || []).map((c: any) => c.getColId() as string);

    const hiddenIds = new Set<string>();
    for (const id of consider) {
      const col = this.gridApi.getColumn(id);
      if (col && !col.isVisible()) {
        hiddenIds.add(String(id).toLowerCase());
      }
    }
    // Locked hierarchy keys are always treated as non-visible data columns.
    for (const locked of this.lockedHierarchyGroupCols) {
      hiddenIds.add(locked.toLowerCase());
    }
    if (hiddenIds.size === 0) {
      return;
    }

    const valueIds = (this.gridApi.getValueColumns() || [])
      .map((c: any) => c.getColId() as string)
      .filter((id: string) => !hiddenIds.has(String(id).toLowerCase()));
    const currentValueIds = (this.gridApi.getValueColumns() || [])
      .map((c: any) => c.getColId() as string);
    if (valueIds.length !== currentValueIds.length) {
      this.gridApi.setValueColumns(valueIds);
    }

    const pivotIds = (this.gridApi.getPivotColumns() || [])
      .map((c: any) => c.getColId() as string)
      .filter((id: string) => !hiddenIds.has(String(id).toLowerCase()));
    const currentPivotIds = (this.gridApi.getPivotColumns() || [])
      .map((c: any) => c.getColId() as string);
    if (pivotIds.length !== currentPivotIds.length) {
      this.gridApi.setPivotColumns(pivotIds);
    }
  }

  private setupDropPanelScroll() {
    if (!this.gridApi || this.dropPanelScrollSetup) {
      return;
    }

    const root = this.elementRef.nativeElement as HTMLElement;
    // AG Grid v35 uses ag-column-drop-rowgroup / ag-column-drop-pivot (not row-group)
    const rowGroupWrapper =
      root.querySelector('.ag-column-drop-rowgroup.ag-column-drop-horizontal') as HTMLElement | null
      || root.querySelector('.ag-column-drop-horizontal-rowgroup') as HTMLElement | null
      || root.querySelector('.ag-column-drop-wrapper > .ag-column-drop-horizontal:not(.ag-column-drop-pivot):not(.ag-hidden)') as HTMLElement | null;
    const pivotWrapper =
      root.querySelector('.ag-column-drop-pivot.ag-column-drop-horizontal') as HTMLElement | null
      || root.querySelector('.ag-column-drop-horizontal-pivot') as HTMLElement | null;

    // Fallback: all horizontal drop panels in the header wrapper
    const allHorizontal = Array.from(
      root.querySelectorAll('.ag-column-drop-wrapper > .ag-column-drop.ag-column-drop-horizontal')
    ) as HTMLElement[];

    if (!rowGroupWrapper && !pivotWrapper && allHorizontal.length === 0) {
      if (this.dropPanelScrollSetupRetries < 20) {
        this.dropPanelScrollSetupRetries++;
        setTimeout(() => {
          if (!this.dropPanelScrollSetup) {
            this.setupDropPanelScroll();
          }
        }, 150);
      }
      return;
    }

    this.dropPanelScrollSetupRetries = 0;

    const panels = new Set<HTMLElement>();
    if (rowGroupWrapper) {
      panels.add(rowGroupWrapper);
    }
    if (pivotWrapper) {
      panels.add(pivotWrapper);
    }
    allHorizontal.forEach(p => panels.add(p));

    panels.forEach(panel => {
      panel.classList.add('drop-panel-scroll-host');
      this.applyDropListScrollClass(panel);
    });

    // Prev/Next only on the first (row-group) panel
    const rowPanel = rowGroupWrapper || allHorizontal[0] || null;
    if (rowPanel) {
      this.setupRowGroupPrevNext(rowPanel);
    }

    this.dropPanelScrollSetup = true;
  }

  private applyDropListScrollClass(dropWrapper: HTMLElement): HTMLElement | null {
    const list =
      dropWrapper.querySelector('.ag-column-drop-list') as HTMLElement | null
      || dropWrapper.querySelector('.ag-column-drop-horizontal-list') as HTMLElement | null
      || dropWrapper;

    if (!list) {
      return null;
    }

    list.classList.add('drop-panel-list-scroll');
    return list;
  }

  private setupRowGroupPrevNext(dropWrapper: HTMLElement) {
    // Scroll the outer drop panel — AG Grid clips here with overflow:hidden
    const scrollTarget = dropWrapper;
    const list = this.applyDropListScrollClass(dropWrapper);
    if (!list) {
      return;
    }

    // Avoid double-injection of Prev/Next
    if (dropWrapper.querySelector('.row-group-scroll-btn')) {
      this.rowGroupScrollList = scrollTarget;
      this.rowGroupPrevBtn = dropWrapper.querySelector('.row-group-scroll-btn--prev') as HTMLButtonElement;
      this.rowGroupNextBtn = dropWrapper.querySelector('.row-group-scroll-btn--next') as HTMLButtonElement;
      this.updateRowGroupScrollButtons();
      return;
    }

    dropWrapper.classList.add('row-group-drop-scrollable');
    list.classList.add('row-group-drop-list-scroll');

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'row-group-scroll-btn row-group-scroll-btn--prev';
    prevBtn.title = 'Previous columns';
    prevBtn.setAttribute('aria-label', 'Previous columns');
    prevBtn.innerHTML = '<i class="bi bi-chevron-left"></i>';
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.scrollRowGroupPanel(-1);
    });

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'row-group-scroll-btn row-group-scroll-btn--next';
    nextBtn.title = 'Next columns';
    nextBtn.setAttribute('aria-label', 'Next columns');
    nextBtn.innerHTML = '<i class="bi bi-chevron-right"></i>';
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.scrollRowGroupPanel(1);
    });

    // Place buttons around the chip list without breaking AG Grid's icon/list layout
    const listParent = list.parentElement || dropWrapper;
    listParent.insertBefore(prevBtn, list);
    if (list.nextSibling) {
      listParent.insertBefore(nextBtn, list.nextSibling);
    } else {
      listParent.appendChild(nextBtn);
    }

    scrollTarget.addEventListener('scroll', () => this.updateRowGroupScrollButtons());
    list.addEventListener('scroll', () => this.updateRowGroupScrollButtons());

    if (typeof ResizeObserver !== 'undefined') {
      this.rowGroupResizeObserver?.disconnect();
      this.rowGroupResizeObserver = new ResizeObserver(() => this.updateRowGroupScrollButtons());
      this.rowGroupResizeObserver.observe(scrollTarget);
      this.rowGroupResizeObserver.observe(list);
    }

    this.rowGroupScrollList = scrollTarget;
    this.rowGroupPrevBtn = prevBtn;
    this.rowGroupNextBtn = nextBtn;
    this.updateRowGroupScrollButtons();
  }

  private scrollRowGroupPanel(direction: number) {
    const list = this.rowGroupScrollList;
    if (!list) {
      return;
    }
    const amount = Math.max(120, Math.floor(list.clientWidth * 0.6));
    list.scrollBy({ left: direction * amount, behavior: 'smooth' });
    setTimeout(() => this.updateRowGroupScrollButtons(), 250);
  }

  private updateRowGroupScrollButtons() {
    const list = this.rowGroupScrollList;
    const prev = this.rowGroupPrevBtn;
    const next = this.rowGroupNextBtn;
    if (!list || !prev || !next) {
      return;
    }

    const maxScroll = list.scrollWidth - list.clientWidth;
    const overflow = maxScroll > 2;
    const atStart = list.scrollLeft <= 2;
    const atEnd = list.scrollLeft >= maxScroll - 2;

    prev.disabled = !overflow || atStart;
    next.disabled = !overflow || atEnd;
    prev.classList.toggle('row-group-scroll-btn--hidden', !overflow);
    next.classList.toggle('row-group-scroll-btn--hidden', !overflow);
  }

  private buildGridState(): string {
    const columnState = this.gridApi.getColumnState() ?? [];
    const state = {
      columnState,
      filterModel: this.gridApi.getFilterModel(),
      rowGroups: this.gridApi.getRowGroupColumns().map((c: any) => c.getColId()),
      valueColumns: this.gridApi.getValueColumns().map((c: any) => c.getColId()),
      pivotColumns: this.gridApi.getPivotColumns().map((c: any) => c.getColId()),
      pivotMode: this.gridApi.isPivotMode(),
      calculatedColumns: this.getCalculatedColumnsPayload()
    };
    return JSON.stringify(state);
  }

  saveReport() {
    if (!this.gridApi) {
      console.warn("Grid not ready");
      return;
    }

    if (!this.reportName?.trim()) {
      this.alertService.error('Please enter report name');
      return;
    }

    const name = this.reportName.trim();
    const exists = this.savedReports.some(
      r => r.Name.trim().toLowerCase() === name.toLowerCase()
      // && Number(r.Id) !== Number(this.selectedSavedReportId)
    );

    if (exists) {
      this.alertService.error(
        `Report name '${name}' already exists. Please choose another name.`
      );
      return;
    }

    this.apiFetch(`${this.apiUrl}/ImpactReport/SaveUserReport`, {
      method: 'POST',
      body: JSON.stringify({
        ReportId: this.selectedReportId,
        ReportName: name,
        GridState: this.buildGridState()
      })
    })
      .then(async res => {
        if (!res.ok) {
          throw new Error('Error saving report');
        }
        this.alertService.success('Report saved');
        this.showSaveBox = false;
        this.reportName = name;
        await this.reloadSavedReportsAndSelectByName(name);
      })
      .catch(error => {
        if (error?.message !== 'Unauthorized') {
          this.alertService.error(error?.message || 'Error saving report');
        }
      });
  }

  requestUpdateReport() {
    if (!this.selectedSavedReportId) {
      return;
    }
    if (!this.gridApi) {
      this.alertService.error('Grid not ready');
      return;
    }
    if (!this.reportName?.trim()) {
      this.alertService.error('Please enter report name');
      return;
    }
    this.showUpdateConfirm = true;
  }

  cancelUpdateConfirm() {
    if (this.isUpdating) {
      return;
    }
    this.showUpdateConfirm = false;
  }

  confirmUpdateReport() {
    if (!this.selectedSavedReportId || !this.gridApi || this.isUpdating) {
      return;
    }

    const name = this.reportName.trim();
    const duplicate = this.savedReports.some(
      r => r.Name.trim().toLowerCase() === name.toLowerCase()
        && Number(r.Id) !== Number(this.selectedSavedReportId)
    );
    if (duplicate) {
      this.showUpdateConfirm = false;
      this.alertService.error(
        `Report name '${name}' already exists. Please choose another name.`
      );
      return;
    }

    this.isUpdating = true;

    this.apiFetch(`${this.apiUrl}/ImpactReport/UpdateUserReport`, {
      method: 'POST',
      body: JSON.stringify({
        Id: this.selectedSavedReportId,
        ReportId: this.selectedReportId,
        ReportName: name,
        GridState: this.buildGridState()
      })
    })
      .then(async res => {
        if (!res.ok) {
          let message = 'Error updating report';
          try {
            const text = await res.text();
            if (text) {
              try {
                const parsed = JSON.parse(text);
                message = typeof parsed === 'string' ? parsed : (parsed.message || message);
              } catch {
                message = text.replace(/^"|"$/g, '');
              }
            }
          } catch {
            // keep default
          }
          throw new Error(message);
        }
        this.alertService.success('Report updated');
        this.showUpdateConfirm = false;
        await this.loadSavedReports();
      })
      .catch(error => {
        if (error?.message !== 'Unauthorized') {
          this.alertService.error(error?.message || 'Error updating report');
        }
      })
      .finally(() => {
        this.isUpdating = false;
      });
  }

  loadSavedReport(id: number | string) {
    this.showAppLoader();
    this.apiFetch(`${this.apiUrl}/ImpactReport/LoadUserReport?id=${id}`)
      .then(res => res.json())
      .then(data => {
        const state = JSON.parse(data);

        this.restoreCalculatedColumns(state.calculatedColumns);

        // Restores pin left/right from saved columnState (wins over soft starter pins).
        if (state.columnState?.length) {
          this.gridApi.applyColumnState({
            state: state.columnState,
            applyOrder: true
          });
        }

        if (state.filterModel) {
          this.gridApi.setFilterModel(state.filterModel);
        }

        if (state.pivotMode !== undefined) {
          this.gridApi.setGridOption('pivotMode', state.pivotMode);
        }

        if (state.rowGroups?.length) {
          this.gridApi.setRowGroupColumns(state.rowGroups);
        }
        this.applyLockedHierarchyGroup();

        if (state.valueColumns?.length) {
          this.gridApi.setValueColumns(state.valueColumns);
        }

        if (state.pivotColumns?.length) {
          this.gridApi.setPivotColumns(state.pivotColumns);
        }

        this.gridApi.refreshServerSide({ purge: true });

      })
      .catch(() => this.hideAppLoader());

  }

  onSavedReportChange(value: string | number | null) {
    if (value === '' || value === null || value === undefined) {
      this.selectedSavedReportId = null;
      return;
    }

    const id = Number(value);
    if (!Number.isFinite(id)) {
      this.selectedSavedReportId = null;
      return;
    }

    this.selectedSavedReportId = id;
    const selected = this.savedReports.find(r => Number(r.Id) === id);
    if (selected?.Name) {
      this.reportName = selected.Name;
    }
    this.loadSavedReport(id);
  }

  loadSavedReports(): Promise<void> {
    return this.apiFetch(`${this.apiUrl}/ImpactReport/GetUserReports?reportId=${this.selectedReportId}`)
      .then(res => res.json())
      .then(data => {
        this.savedReports = data ?? [];
      });
  }

  private async reloadSavedReportsAndSelectByName(name: string): Promise<void> {
    await this.loadSavedReports();
    const match = this.savedReports.find(
      r => String(r.Name).trim().toLowerCase() === name.toLowerCase()
    );
    if (match) {
      this.selectedSavedReportId = Number(match.Id);
    }
  }

  loadCurrentUser() {
    this.apiFetch(`${this.apiUrl}/Auth/current-user`)
      .then(res => res.json())
      .then(data => {
        this.selectedUserId = data.ReportUserId;
        this.ReportAPIId = data.ReportAPIId;
        this.displayUserName = data.ReportuserName;

        this.loadReport(this.ReportAPIId);
      })
      .catch(error => {
        this.hideAppLoader();
        if (error.message !== 'Unauthorized') {
          this.alertService.error('Authentication Error: ' + error);
        }
      });
  }

  resetReport() {

    if (!this.gridApi) {
      return;
    }

    this.showAppLoader();

    // Clear filters
    this.gridApi.setFilterModel(null);

    // Disable pivot mode
    this.gridApi.setGridOption('pivotMode', false);

    // Remove extra row groups; keep locked parent key for hierarchical reports
    this.gridApi.setRowGroupColumns([]);
    this.applyLockedHierarchyGroup();

    // Remove pivot columns
    this.gridApi.setPivotColumns([]);

    this.calculatedColumns = [];
    this.calcName = '';
    this.calcFormula = '';
    this.calcError = '';
    this.showCalcDialog = false;
    this.editingCalcName = null;
    this.syncCalculatedColumnDefs();

    // Reset column state
    this.gridApi.resetColumnState();

    // Keep default aggregate value columns (required for pivot / grouping)
    this.restoreDefaultAggregates();
    this.applyLockedHierarchyGroup();
    this.applyStarterLeftPinsIfNeeded();

    // Clear selected saved report dropdown
    this.selectedReportId = Number(this.ReportAPIId);
    this.selectedSavedReportId = null;
    this.reportName = '';
    this.showUpdateConfirm = false;

    // Reload original report
    this.gridApi.refreshServerSide({ purge: true });

    this.updateRowGroupScrollButtons();

    this.alertService.success('Report has been reset to its default state.');
  }

  exportToExcel() {
    if (!this.gridApi || !this.selectedReportId || this.isExporting) {
      return;
    }

    const columnState = this.gridApi.getColumnState() ?? [];
    const sortModel = columnState
      .filter((c: { sort?: string | null }) => !!c.sort)
      .sort((a: { sortIndex?: number | null }, b: { sortIndex?: number | null }) =>
        (a.sortIndex ?? 0) - (b.sortIndex ?? 0)
      )
      .map((c: { colId: string; sort: string }) => ({
        ColId: c.colId,
        Sort: c.sort
      }));

    const visibleColumns = columnState
      .filter((c: { hide?: boolean }) => !c.hide)
      .map((c: { colId: string }) => c.colId);

    const body = {
      ReportId: this.selectedReportId,

      FilterModel: this.gridApi.getFilterModel() ?? {},

      SortModel: sortModel,

      RowGroupCols: this.gridApi.getRowGroupColumns().map((c: any) => ({
        field: c.getColId()
      })),

      ValueCols: this.gridApi.getValueColumns().map((c: any) => ({
        field: c.getColId(),
        aggFunc: c.getAggFunc()
      })),

      PivotCols: this.gridApi.getPivotColumns().map((c: any) => ({
        field: c.getColId()
      })),

      // Same rule as SSRM: Pivot Mode toggle alone is not enough without Column Labels.
      PivotMode: this.gridApi.isPivotMode()
        && this.gridApi.getPivotColumns().length > 0,

      ColumnState: columnState,

      VisibleColumns: visibleColumns,

      CalculatedColumns: this.getCalculatedColumnsPayload()
    };

    this.isExporting = true;

    this.apiFetch(`${this.apiUrl}/ImpactReport/ExportMovieSalesExcel`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
      .then(async res => {
        if (!res.ok) {
          let message = 'Error exporting Excel file';
          try {
            const text = await res.text();
            if (text) {
              // API may return plain string or JSON string
              try {
                const parsed = JSON.parse(text);
                message = typeof parsed === 'string' ? parsed : (parsed.message || message);
              } catch {
                message = text.replace(/^"|"$/g, '');
              }
            }
          } catch {
            // keep default message
          }
          throw new Error(message);
        }
        return res.blob();
      })
      .then(blob => {
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        const fileName = `AnalysisReport_${stamp}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        this.alertService.success('Excel export downloaded.');
      })
      .catch(error => {
        if (error?.message !== 'Unauthorized') {
          this.alertService.error(error?.message || 'Error exporting Excel file');
        }
      })
      .finally(() => {
        this.isExporting = false;
      });
  }

  logout() {
    fetch(`${this.apiUrl}/Auth/logout`, {
      method: 'POST',
      credentials: 'include'
    }).finally(() => {

      // Close analysis tab
      window.close();

      // Fallback if browser blocks close
      window.location.href = 'about:blank';
    });
  }
}